import { nextTick, ref, shallowRef, computed } from 'vue';
import { defineStore } from 'pinia';
import { useRouter } from "vue-router";
import { useNotifications } from "./notifications.store";
import { useBuilder } from "./builder.store";
import { useIsMobile } from "../composables/isMobile";

import communityAPI from "../api/community";

const LAZY_LOADING_FIRST_PAGE = 1;
const DEFAULT_PAGINATION_STATE = {
    "total": 0,
    "per_page": 10,
    "current_page": 1,
    "last_page": 1
}

export const useCommunityStore = defineStore('community', () => {
    const router = useRouter();
	const { isMobile } = useIsMobile();
	
    const notificationsStore = useNotifications();
    const builderStore = useBuilder();
    
    const sharedResumes = ref([]);
    const savedResumes = ref([]);
    const userResumes = ref([]);
    
    const loadingAddComment = shallowRef(false);
    const loadingPublish = shallowRef(false);
    const loadingFetchingResumes = shallowRef(false);
    const loadingFetchComments = shallowRef(false);
    const loadingSearch = shallowRef(false);
    const loadingReportComment = shallowRef(false);
    
    const searchQuery = ref(null);
    const searchResult = ref([]);
    const showSearchResult = shallowRef(false);
    
    const showDocumentPreview = shallowRef(false);
	const showMobileDisclaimer = shallowRef(false);
    
    const commentInputValue = shallowRef(null);
    const commentsForCurrentSnapshot = ref(null);
    const activeSnapshotId = ref(null);
    
    const currentSearchQuery = shallowRef(null);
    const showSearchResults = shallowRef(false);
    const activePage = ref(null);
    
    const showReportCommentModal = shallowRef(false);
    const selectedCommentId = ref(null);
    
    const commentInputRef = ref(null);
	let resumeBuffer = null;
    
    const paginationState = shallowRef(DEFAULT_PAGINATION_STATE);
    const lazyLoading = ref(false)
	
	const setShowMobileDisclaimer = (resumeToShow) => {
		resumeBuffer = resumeToShow;
		showMobileDisclaimer.value = true;
	}
	
	const hideMobileDisclaimer = () => {
		showMobileDisclaimer.value = false;
	}
	
	const ignoreMobileDisclaimerHandler = async () => {
		hideMobileDisclaimer();
		
		await setShowDocumentPreview(resumeBuffer, true);
		resumeBuffer = null;
	}
    
    const setShowReportComment = (commentId) => {
        selectedCommentId.value = commentId;
        showReportCommentModal.value = true;
    }
    
    const hideReportComment = () => {
        selectedCommentId.value = null;
        showReportCommentModal.value = false;
    }
    
    const setActivePage = (page) => {
        paginationState.value = DEFAULT_PAGINATION_STATE;
        activePage.value = page;
    }
    
    const setShowDocumentPreview = async (resume, force) => {
		if(isMobile.value && !force) {
			setShowMobileDisclaimer(resume);
			return;
		}

		activeSnapshotId.value = resume.uuid;
		
        await router.replace({query: { resume: resume?.uuid }});
        
        if(resume?.meta?.resume_file?.path == null) {
            await builderStore.initUsingObject(resume?.resume_data, {
                isDocumentEditable: false,
                showPersonalInformation: false
            });
        }
        
        await nextTick();
		showDocumentPreview.value = true;
    }
    
    const showResumeFromShareLink = async (resumeId) => {
        const resume = sharedResumes.value.find(r => r?.uuid === resumeId)
        await setShowDocumentPreview(resume, { addIdToQuery: true });
    }

    const hideDocumentPreview = async () => {
        showDocumentPreview.value = false;
        
        await nextTick();

        if(activeSnapshot?.meta?.resume_file == null) {
            builderStore.resetState();
        }
        
        await router.replace({ query: {} });
        activeSnapshotId.value = null;
    }

    const clearSearchHandler = async () => {
        paginationState.value = DEFAULT_PAGINATION_STATE;
        await fetchCommunityResumes();
        searchQuery.value = null;
        showSearchResults.value = false;
        searchResult.value = [];
    }

    const getPaginationQuery = (forceFirstPage = false) => {
        if(forceFirstPage) {
            return `?page=${0}`;
        }

        if(ableToLoadMore.value) {
            return `?page=${paginationState.value.current_page + 1}`;
        }

        return `?page=${LAZY_LOADING_FIRST_PAGE}`;
    }
    
    const searchResumesHandler = async (isLazyLoading = false) => {
        loadingSearch.value = true;
        
        if(isLazyLoading) {
            lazyLoading.value = true;
        }else{
            loadingFetchingResumes.value = true;
        }
        
        const urlQuery = `${getPaginationQuery(!isLazyLoading)}&query=${isLazyLoading ? currentSearchQuery.value : searchQuery.value}`;
        
        try {
            const response = await communityAPI.resume.search(urlQuery);

            if(isLazyLoading) {
                searchResult.value.push(...response.data);
            }else{
                searchResult.value = response.data;
            }

            paginationState.value = response.pagination;
            
            showSearchResults.value = true;
            currentSearchQuery.value = searchQuery.value;
        }catch (e) {
            notificationsStore.notify("Failed to search!", 'error')
        }finally {
            lazyLoading.value = false;
            loadingSearch.value = false;
            loadingFetchingResumes.value = false;
        }
    }
	
	const updateResumesList = (snapshotId, updatedResume) => {
		let newValue = []

		if(activePage.value === 'dashboard') {
			newValue = showSearchResult.value ? searchResult.value : sharedResumes.value;
		}else if(activePage.value === 'saved') {
			newValue = savedResumes.value
		}else if(activePage.value === 'profile') {
			newValue = userResumes.value
		}

		const resumeIndex = newValue.findIndex(r => r.uuid === snapshotId);
		newValue.splice(resumeIndex, 1, updatedResume);

		if(activePage.value === 'dashboard') {
			if(showSearchResult.value){
				searchResult.value = newValue;
			}else{
				sharedResumes.value = newValue;
			}
		}else if(activePage.value === 'saved') {
			savedResumes.value = newValue
		}else if(activePage.value === 'profile') {
			userResumes.value = newValue
		}
	}

    const addResumeToBookmarks = async (snapshotId) => {
        try {
            const response = await communityAPI.addResumeToBookmarks(snapshotId);
            updateResumesList(snapshotId, response.resume_snapshot);
        }catch (error) {
            notificationsStore.notify("Failed to save the resume to bookmarks!", 'error')
        }
    }

    const fetchSavedCommunityResumes = async (isLazyLoading = false) => {
        if(isLazyLoading) {
            lazyLoading.value = true
        }else{
            loadingFetchingResumes.value = true;
        }
        
        try {
            const response = await communityAPI.getSavedResumes(getPaginationQuery(0));

            const resumes = response.data.map(resume => {
                return resume.resume_snapshot
            })
            
            if(isLazyLoading) {
                savedResumes.value.push(...resumes);
            }else{
                savedResumes.value = resumes;
            }

            paginationState.value = response.pagination;
        }catch (e) {
            notificationsStore.notify("Failed to fetch resumes! Please try again.", 'error')
        } finally {
            loadingFetchingResumes.value = false;
        }
    }

    const fetchCommunityResumes = async (isLazyLoading = false) => {
        if(isLazyLoading) {
            lazyLoading.value = true
        }else{
            loadingFetchingResumes.value = true;
        }
        
        try {
            const response = await communityAPI.getPublishedResumes(getPaginationQuery());
            
            if(isLazyLoading) {
                sharedResumes.value.push(...response.data);
            }else{
                sharedResumes.value = response.data;
            }
            
            paginationState.value = response.pagination;
        }catch (e) {
            notificationsStore.notify("Failed to fetch resumes! Please try again.", 'error')
        } finally {
            loadingFetchingResumes.value = false;
            lazyLoading.value = false;
        }
    }
    
    const publishResume = async (body) => {
        loadingPublish.value = true;
        
        try {
            await communityAPI.publishResume(body);
        }catch (e) {
            notificationsStore.notify("Failed to publish resume! Please try again.", 'error')
        }finally {
            loadingPublish.value = false;
        }
    }
    
    const postComment = async () => {
        loadingAddComment.value = true;
        
        try {
            const response = await communityAPI.comments.add(activeSnapshot.value.uuid, commentInputValue.value);

            commentsForCurrentSnapshot.value.push(response);
            commentInputValue.value = null;

            const resume = getSnapshotById(activeSnapshot.value.uuid);

            updateResumesList(activeSnapshot.value.uuid, {
                ...resume,
                meta: {
                    ...resume.meta,
                    total_comments: resume.meta.total_comments + 1
                }
            })

			notificationsStore.notify("Comment was added successfully!", 'success');
        }catch (error) {
            if(error?.response?.status === 403) {
                notificationsStore.notify(e.response.data.message, 'error')
            }else{
				notificationsStore.notify("Failed to post your comment!", 'error')
			}
        }finally {
            loadingAddComment.value = false;
        }
    }
	
	const getSnapshotById = (snapshotId) => {
		let resumesList = [];
		if(activePage.value === 'dashboard') {
			resumesList = showSearchResult.value ? searchResult.value : sharedResumes.value;
		}else if(activePage.value === 'saved') {
			resumesList = savedResumes.value
		}else if(activePage.value === 'profile') {
			resumesList = userResumes.value
		}
		
		return resumesList.find(snapshot => snapshot?.uuid === snapshotId)
	}
    
    const getAllCommentsForCurrentSnapshot = async () => {
        loadingFetchComments.value = true;
        
        try {
            commentsForCurrentSnapshot.value = await communityAPI.comments.getAll(activeSnapshotId.value);
        }catch (e) {
            notificationsStore.notify("Failed to get comments for current resume!", 'error')
        }finally {
            loadingFetchComments.value = false;
        }
    }
    
    const duplicateResume = async (snapshotId) => {
        try {
            await communityAPI.resume.duplicate(snapshotId || activeSnapshot.value?.uuid);
			
			const snapshotObj = getSnapshotById(snapshotId);
			if(snapshotObj != null) {
				updateResumesList(snapshotId,{
				...snapshotObj,
					meta: {
					...snapshotObj.meta,
							total_cloned_resumes: snapshotObj.meta.total_cloned_resumes + 1
					}
				})
			}

            notificationsStore.notify(
                'This resume was copied successfully to your ResumAI page!',
                "success",
                3500,
                null,
                "Click here to check it out.",
                () => router.push('/resumes')
            )
        }catch (e) {
            notificationsStore.notify("Failed to duplicate!", 'error')
        }
    }
    
    const fetchPersonalResumes = async (isLazyLoading = false) => {
        try {
            if(isLazyLoading) {
                lazyLoading.value = true
            }else{
                loadingFetchingResumes.value = true;
            }
            
            const response = await communityAPI.resume.search(`${getPaginationQuery()}&owner=current_user`);

            if(isLazyLoading) {
                userResumes.value.push(...response.data);
            }else{
                userResumes.value = response.data;
            }

            paginationState.value = response.pagination;
        }catch (e) {
            notificationsStore.notify("Failed to fetch resumes! Please try again.", 'error');
        } finally {
            loadingFetchingResumes.value = false;
        }
    }
    
    const calculateVotesFrom  = (currentState, vote, isUpVotedByCurrentUser, isDownVotedByCurrentUser) => {
        let newTotalUpVotes = currentState.total_upvotes
        let newTotalDownVotes = currentState.total_downvotes
        
        if(vote) {
            newTotalUpVotes = newTotalUpVotes + 1
            
            if(isDownVotedByCurrentUser){
                newTotalDownVotes = newTotalDownVotes - 1
            }
        }else{
            newTotalDownVotes = newTotalDownVotes + 1

            if(isUpVotedByCurrentUser){
                newTotalUpVotes = newTotalUpVotes - 1
            }
        }
        
        return {
            total_upvotes: newTotalUpVotes,
            total_downvotes: newTotalDownVotes
        }
    }
    
    const resumeVoteHandler = async (resumeId, vote) => {
        try {
            await communityAPI.resume.vote(resumeId, vote);
            
            const resume = getSnapshotById(resumeId);

            const {
                total_upvotes,
                total_downvotes,
                is_downvoted_by_current_user,
                is_upvoted_by_current_user,
            } = resume.meta
            
            const newVotesState = calculateVotesFrom ({
                total_upvotes,
                total_downvotes
            }, vote, is_upvoted_by_current_user, is_downvoted_by_current_user)
            
			updateResumesList(resumeId, {
                ...resume,
                meta: {
                    ...resume.meta,
                    is_downvoted_by_current_user: !vote,
                    is_upvoted_by_current_user: !!vote,
                    ...newVotesState
                }
            })
        }catch (e) {
            notificationsStore.notify("Failed to vote!", 'error')
        }
    }
	
	const deleteCommentHandler = async (commentId) => {
		try {
			await communityAPI.comments.delete(commentId);
			
			const commentIndex = commentsForCurrentSnapshot.value.findIndex(c => c.id === commentId);
			const newValue = commentsForCurrentSnapshot.value
			
			newValue.splice(commentIndex, 1);
			
			commentsForCurrentSnapshot.value = newValue;
		}catch (e) {
			notificationsStore.notify("Failed to delete comment! Please try again.", 'error')
		}
	}
    
    const reportCommentHandler = async (reportBody) => {
        loadingReportComment.value = true;
        
        try {
            await communityAPI.comments.report(selectedCommentId.value, reportBody);
            hideReportComment();

            notificationsStore.notify("Thanks for flagging the comment, it is currently under review by our moderators!")
        }catch (e) {
            notificationsStore.notify("Failed to report comment! Please try again.", 'error')
        }finally {
            loadingReportComment.value = false;
        }
    }

    const activeSnapshot = computed(() => {
        if(activeSnapshotId.value == null) return null;
        
		let searchIn = [];
	
		if(activePage.value === 'dashboard') {
			searchIn = showSearchResult.value ? searchResult.value : sharedResumes.value;
		}else if(activePage.value === 'saved') {
			searchIn = savedResumes.value;
		}else if(activePage.value === 'profile') {
			searchIn = userResumes.value;
		}

		return searchIn.find(r => r.uuid === activeSnapshotId.value)
	});
    
    const ableToLoadMore = computed(() => {
        const { current_page, last_page  } = paginationState.value;
        
        return current_page < last_page;
    })
    
    return {
        showDocumentPreview,
        sharedResumes,
        savedResumes,
        searchResult,
        commentInputValue,
        commentInputRef,
        loadingAddComment,
        loadingReportComment,
        loadingPublish,
        commentsForCurrentSnapshot,
        activeSnapshot,
        currentSearchQuery,
        loadingSearch,
        showSearchResults,
        userResumes,
        loadingFetchComments,
        searchQuery,
        showReportCommentModal,
        loadingFetchingResumes,
		showMobileDisclaimer,
        paginationState,
        
        setShowReportComment,
        hideReportComment,
		setShowMobileDisclaimer,
		hideMobileDisclaimer,
		ignoreMobileDisclaimerHandler,

        publishResume,
        clearSearchHandler,
        fetchCommunityResumes,
        fetchSavedCommunityResumes,
        addResumeToBookmarks,
        setActivePage,
        ableToLoadMore,
        lazyLoading,
        
        setShowDocumentPreview,
        showResumeFromShareLink,
        hideDocumentPreview,
        comments: {
            add: postComment,
            getAll: getAllCommentsForCurrentSnapshot,
			delete: deleteCommentHandler,
            report: reportCommentHandler
        },
        resume: {
            vote: resumeVoteHandler,
            search: searchResumesHandler,
            duplicate: duplicateResume,
            getUserResumes: fetchPersonalResumes
        }
    }
})
